import { Link as RouterLink } from 'react-router-dom';

import Button from 'components/partials/button/button';

import { getIconByFileType } from 'components/partials/file-uploader/file-uploader.helper';
import { formatDate } from 'helpers/dates-formatter';
import { getStatusLabel } from 'components/partials/table-component/helpers/get-status-label';

import { FILES_STATUSES } from 'containers/admin/clients/client/tabs-components/files-tab/files-tab.constants';

import Icons from 'assets/icons';

export interface FileUserShort {
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
}

export interface FileRow {
  id: string | number;
  fileExt?: string;
  name?: string;
  description?: string;
  status?: string;
  createdDate?: string;
  isAvailableTo?: FileUserShort[];
  [key: string]: unknown;
}

interface InfoArg {
  getValue: () => unknown;
  renderValue: () => React.ReactNode;
  row: { original: FileRow };
}

interface FilesColumnDef {
  id: string;
  accessorKey?: string;
  header: () => React.JSX.Element;
  cell: (info: InfoArg) => React.ReactNode;
  headerClassName?: string;
  className?: string;
  enableSorting?: boolean;
  sortingFn?: string;
}

const commonStyles = 'pl-4 border-r-2 border-white font-semibold';

export const getFilesTableSettings = (
  clientId: string | number | undefined
): FilesColumnDef[] =>
  [
    {
      id: 'fileExt',
      accessorKey: 'fileExt',
      header: () => <span>FILE TYPE</span>,
      cell: (info: InfoArg) =>
        getIconByFileType(info.row.original.fileExt ?? ''),
      headerClassName: `${commonStyles} rounded-tl-md rounded-bl-md cursor-context-menu`,
      className: 'xs:w-[10%] sm:w-[9%] md:w-[7%] xl:w-[6%]',
      enableSorting: false,
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: () => <span>FILE NAME</span>,
      cell: (info: InfoArg) => {
        const label = getStatusLabel(
          info.row.original.status ?? '',
          FILES_STATUSES
        );

        return (
          <div className="flex flex-col py-3">
            <span className="w-full pr-2 pb-3 font-semibold">
              {info.renderValue() as React.ReactNode}
            </span>
            {label}
          </div>
        );
      },
      headerClassName: `${commonStyles} cursor-pointer`,
      className: 'pl-4 xs:w-[19%] sm:w-[16%] md:w-[17%]',
      enableSorting: true,
      sortingFn: 'alphanumeric',
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: () => <span>DESCRIPTION</span>,
      cell: (info: InfoArg) => (
        <span>{info.renderValue() as React.ReactNode}</span>
      ),
      headerClassName: `${commonStyles} cursor-context-menu`,
      className: 'pl-4 xs:w-[21%] sm:w-[20%] lg:w-[20%]',
      enableSorting: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: () => <span>UPLOAD DATE</span>,
      cell: (info: InfoArg) =>
        formatDate(info.row.original.createdDate, 'MMM D, YYYY h:mm A'),
      headerClassName: `${commonStyles} cursor-pointer`,
      className: 'pl-4 xs:w-[15%]',
      enableSorting: true,
    },
    {
      id: 'isAvailableTo',
      accessorKey: 'isAvailableTo',
      header: () => <span>FILE IS AVAILABLE TO</span>,
      cell: (info: InfoArg) => (
        <div>
          {info?.row?.original?.isAvailableTo
            ?.map((user) => `${user.firstName} ${user.lastName}`)
            .slice(0, 4)
            .join(', ')}
        </div>
      ),
      headerClassName: `${commonStyles} cursor-context-menu`,
      className: 'pl-4 pt-3 pb-3 xs:w-[19%] sm:w-[17%]',
      enableSorting: false,
    },
    {
      id: 'button-cell',
      header: () => <span></span>,
      cell: ({
        row: {
          original: { id },
        },
      }: InfoArg) => (
        <RouterLink to={`/admin/clients/${clientId}/files/${id}`}>
          <Button
            variant="reserve-outlined"
            size="sm"
            iconBefore={<Icons.Eye aria-label="EyeIcon" />}
            className="text-xs md:text-sm"
          >
            View Details
          </Button>
        </RouterLink>
      ),
      headerClassName: 'h-12 rounded-tr-md rounded-br-md pl-4',
      className:
        'text-center pl-4 xs:w-[16%] sm:w-[15%] md:w-[12%] lg:w-[10%] xl:w-[9%]',
    },
  ].filter(Boolean) as FilesColumnDef[];
